import { memo, useMemo } from 'react';
import * as THREE from 'three';
import { Line, Text } from '@react-three/drei';
import { useAppStore } from '../state/store';
import { useDocumentStore } from '../state/useDocumentStore';
import { resolveVisibility, currentVisibilityInput } from './visibility';
import { dispatchEntityClick, entityMeshRegistry } from './toolEventBus';

/**
 * CAD Reference Layer (Phase B cross-visibility, the mirror image of the
 * BIMOverlayLayer in the shared scene).
 *
 * Mounts inside the BIM viewport's canvas and draws the document's CAD
 * entities as wireframe cross-references, so a line drawn at the CAD
 * workbench is immediately visible while working in BIM — in the same
 * Z-up world frame (A5), selected through the same ledger +
 * VisibilityResolver language (A4). Full-fidelity 3D primitives are left
 * to the shared engine later; plan drafting geometry is what matters for
 * cross-referencing.
 */
const REF_COLOR = '#9ecbff';
const REF_COLOR_SELECTED = '#00aaff';

export const CadReferenceLayer = memo(function CadReferenceLayer() {
    const entities = useAppStore((s) => s.entities);
    const selectedIds = useAppStore((s) => s.selectedIds);
    const ledger = useDocumentStore((s) => s.sceneObjects);

    // Visibility comes from the shared resolver (layer/object/view-scope),
    // never from CAD-store-local checks.
    const visibleIds = useMemo(() => {
        const input = currentVisibilityInput();
        const ids: string[] = [];
        for (const id of Object.keys(entities)) {
            const obj = ledger[id];
            if (!obj) continue;
            const r = resolveVisibility(obj, input);
            if (r.visible) ids.push(id);
        }
        return ids;
    }, [entities, ledger]);

    const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

    return (
        <group>
            {visibleIds.map((id) => (
                <CadReferenceRenderer key={id} entityId={id} isSelected={selectedSet.has(id)} />
            ))}
        </group>
    );
});

const CadReferenceRenderer = memo(function CadReferenceRenderer({
    entityId,
    isSelected,
}: {
    entityId: string;
    isSelected: boolean;
}) {
    const entity = useAppStore((s) => s.entities[entityId]);

    const points = useMemo(() => buildReferencePoints(entity), [entity]);
    const refLabel = useMemo(() => referenceText(entity), [entity]);

    if (!entity) return null;

    const color = isSelected ? REF_COLOR_SELECTED : REF_COLOR;

    const handleClick = (e: { stopPropagation: () => void }) => {
        e.stopPropagation();
        dispatchEntityClick(entityId, e as any);
    };

    const registryRef = (obj: THREE.Object3D | null) => {
        if (obj) {
            obj.userData.entityId = entityId;
            obj.userData.layer = entity.layer;
            entityMeshRegistry.set(entityId, obj);
        } else {
            entityMeshRegistry.delete(entityId);
        }
    };

    return (
        <group ref={registryRef} onClick={handleClick}>
            {points.map((pts, i) => (
                <Line
                    key={i}
                    points={pts}
                    color={color}
                    lineWidth={isSelected ? 2 : 1}
                    transparent
                    opacity={isSelected ? 0.95 : 0.7}
                />
            ))}
            {refLabel && (
                <Text
                    position={refLabel.position}
                    fontSize={0.2}
                    color={color}
                    anchorX="center"
                    anchorY="bottom"
                >
                    {refLabel.content}
                </Text>
            )}
        </group>
    );
});

type Pt3 = [number, number, number];

/**
 * Convert a CAD entity's drafting data to wireframe polylines in the
 * Z-up world frame (all stored data is already in that frame after A5).
 */
export function buildReferencePoints(entity: { type: string; data: Record<string, any> }): Pt3[][] {
    if (!entity) return [];
    const d = entity.data ?? {};
    const num = (v: unknown, fallback = 0): number =>
        typeof v === 'number' && Number.isFinite(v) ? v : fallback;

    switch (entity.type) {
        case 'line':
            if (d.start && d.end) return [[d.start as Pt3, d.end as Pt3]];
            return [];
        case 'rectangle':
            if (d.corner1 && d.corner2) {
                const [x1, y1, z1] = d.corner1 as number[];
                const [x2, y2] = d.corner2 as number[];
                return [[
                    [x1, y1, z1], [x2, y1, z1], [x2, y2, z1], [x1, y2, z1], [x1, y1, z1],
                ]];
            }
            return [];
        case 'polyline':
        case 'polygon':
            if (Array.isArray(d.vertices) && d.vertices.length >= 2) {
                const pts = d.vertices.map((v: number[]) => [num(v[0]), num(v[1]), num(v[2])] as Pt3);
                if (entity.type === 'polygon' || d.closed) pts.push(pts[0]);
                return [pts];
            }
            return [];
        case 'circle':
        case 'arc': {
            if (!Array.isArray(d.center)) return [];
            const [cx, cy, cz] = d.center as number[];
            const r = num(d.radius, 1);
            const startAngle = entity.type === 'arc' ? num(d.startAngle, 0) : 0;
            let endAngle = entity.type === 'arc' ? num(d.endAngle, Math.PI * 2) : Math.PI * 2;
            if (endAngle <= startAngle) endAngle += Math.PI * 2;
            const segments = Math.max(8, Math.round(((endAngle - startAngle) / (Math.PI * 2)) * 64));
            const pts: Pt3[] = [];
            for (let i = 0; i <= segments; i++) {
                const a = startAngle + ((endAngle - startAngle) * i) / segments;
                pts.push([cx + Math.cos(a) * r, cy + Math.sin(a) * r, cz]);
            }
            return [pts];
        }
        case 'ellipse': {
            if (!Array.isArray(d.center)) return [];
            const [cx, cy, cz] = d.center as number[];
            const ra = num(d.radiusA, 1);
            const rb = num(d.radiusB, 1);
            const rot = num(d.rotation, 0);
            const pts: Pt3[] = [];
            const segments = 48;
            for (let i = 0; i <= segments; i++) {
                const t = (i / segments) * Math.PI * 2;
                const x = ra * Math.cos(t);
                const y = rb * Math.sin(t);
                pts.push([
                    cx + x * Math.cos(rot) - y * Math.sin(rot),
                    cy + x * Math.sin(rot) + y * Math.cos(rot),
                    cz,
                ]);
            }
            return [pts];
        }
        case 'curve': {
            if (Array.isArray(d.controlPoints) && d.controlPoints.length >= 2) {
                const vectors = d.controlPoints.map((p: number[]) =>
                    new THREE.Vector3(num(p[0]), num(p[1]), num(p[2])));
                const curve = new THREE.CatmullRomCurve3(vectors, false, 'catmullrom', 0.5);
                return [curve.getPoints(48).map((v) => [v.x, v.y, v.z] as Pt3)];
            }
            return [];
        }
        case 'text':
        case 'mtext': {
            // Text entities render via refLabel below; no polylines needed.
            return [];
        }
        case 'dimension': {
            if (Array.isArray(d.point1) && Array.isArray(d.point2)) {
                return [[d.point1 as Pt3, d.point2 as Pt3]];
            }
            return [];
        }
        // --- command-parity entity kinds ---
        case 'point': {
            if (!Array.isArray(d.position)) return [];
            const [x, y, z] = d.position as number[];
            const s = 0.1;
            return [
                [[x - s, y, z], [x + s, y, z]],
                [[x, y - s, z], [x, y + s, z]],
            ];
        }
        case 'leader': {
            if (!Array.isArray(d.tip) || !Array.isArray(d.landing)) return [];
            const tip = d.tip as Pt3, land = d.landing as Pt3;
            return [[tip, land, [land[0] + 0.3, land[1], land[2]] as Pt3]];
        }
        case 'attdef': {
            if (!Array.isArray(d.position)) return [];
            const [x, y, z] = d.position as number[];
            return [[[x - 0.15, y - 0.15, z], [x + 0.15, y + 0.15, z]], [[x - 0.15, y + 0.15, z], [x + 0.15, y - 0.15, z]]];
        }
        case 'insert': {
            // Block reference: marker box at the insertion point (the full
            // definition renders in its own workbench; here it is a ref).
            const p = Array.isArray(d.position) ? (d.position as number[]) : [0, 0, 0];
            const s = 0.3;
            return [[
                [p[0] - s, p[1] - s, p[2] ?? 0], [p[0] + s, p[1] - s, p[2] ?? 0],
                [p[0] + s, p[1] + s, p[2] ?? 0], [p[0] - s, p[1] + s, p[2] ?? 0],
                [p[0] - s, p[1] - s, p[2] ?? 0],
            ]];
        }
        case 'solid': {
            // Mesh payload: draw naked boundary edges (used once) as the
            // wireframe cross-reference; interior diagonals would clutter it.
            const verts = d.vertices as number[][] | undefined;
            const idx = d.indices as number[][] | undefined;
            if (!Array.isArray(verts) || verts.length < 3) return [];
            if (!Array.isArray(idx) || idx.length === 0) {
                // point-sampled solid (lights): single marker
                const v0 = verts[0];
                return [[[v0[0] - 0.1, v0[1], v0[2] ?? 0], [v0[0] + 0.1, v0[1], v0[2] ?? 0]]];
            }
            const use = new Map<string, { count: number; x: number; y: number }>();
            idx.forEach(([a, b, c]) => {
                ([[a, b], [b, c], [c, a]] as [number, number][]).forEach(([x, y]) => {
                    const key = x < y ? `${x}:${y}` : `${y}:${x}`;
                    const hit = use.get(key);
                    if (hit) hit.count += 1; // interior edge (shared by 2 faces)
                    else use.set(key, { count: 1, x, y });
                });
            });
            const lines: Pt3[][] = [];
            use.forEach((v) => {
                if (v.count !== 1) return;
                const va = verts[v.x], vb = verts[v.y];
                if (va && vb) lines.push([[va[0], va[1], va[2] ?? 0], [vb[0], vb[1], vb[2] ?? 0]]);
            });
            // No boundary (closed solid): outline triangles instead (capped).
            if (lines.length === 0) {
                idx.slice(0, 400).forEach(([a, b, c]) => {
                    const va = verts[a], vb = verts[b], vc = verts[c];
                    if (va && vb && vc) {
                        lines.push([[va[0], va[1], va[2] ?? 0], [vb[0], vb[1], vb[2] ?? 0],
                            [vc[0], vc[1], vc[2] ?? 0], [va[0], va[1], va[2] ?? 0]]);
                    }
                });
            }
            return lines;
        }
        default:
            return [];
    }
}

/** Optional label (single-line text entities). */
function referenceText(entity: {
    type: string;
    data: Record<string, any>;
}): { position: [number, number, number]; content: string } | null {
    if ((entity.type === 'text' || entity.type === 'mtext') && typeof entity.data?.content === 'string') {
        const p = entity.data.position as number[] | undefined;
        if (Array.isArray(p) && p.length >= 3) {
            return { position: [p[0], p[1], p[2] + 0.05], content: entity.data.content };
        }
    }
    return null;
}
